import { unifiedDataStore } from "../../services/UnifiedDataStore";
import type { GameState } from "../../stores/useGameStoreFacade";
import type { GameReport } from "../../utils/gameReportUtils";
import { allAchievements } from "./definitions"; // Adjusted path
import type { Achievement } from "./achievement.types"; // Adjusted path

/**
 * The stored ids the registry still knows. Ids are renamed with their names
 * and retired ones removed outright (spec 2026-10-20, A1/A2), so a device
 * from before a rename may hold unlocks nothing can show; surfaces count
 * through this so "N of 25" and the unviewed badge never count them.
 */
export const knownAchievementIds = (ids: readonly string[]): string[] => {
  const known = new Set(allAchievements.map((a) => a.id));
  return ids.filter((id) => known.has(id));
};

// The tutorial is guided, so it only earns Q.E.D.
const achievementsFor = (gameReport: GameReport): Achievement[] =>
  gameReport.isTutorial
    ? allAchievements.filter((a) => a.id === "qed")
    : allAchievements;

// Function to evaluate all achievements for a given game report and status
export const evaluateAchievements = (
  gameReport: GameReport,
  gameStatus: GameState["gameStatus"],
): Achievement[] => {
  if (!gameReport) {
    return [];
  }
  // Only evaluate for 'won' status, or if an achievement itself doesn't depend on winning.
  // For now, most will, so this is a general guard.
  // Individual achievement 'check' functions are responsible for their specific win condition logic.
  return achievementsFor(gameReport).filter((achievement) =>
    achievement.check(gameReport, gameStatus),
  );
};

// Function to handle progressive achievements - increment counters for achievements that were earned
export const handleProgressiveAchievements = async (
  gameReport: GameReport,
  gameStatus: GameState["gameStatus"],
): Promise<string[]> => {
  if (!gameReport) {
    return [];
  }

  // The tutorial is guided, so it never advances progressive achievements.
  if (gameReport.isTutorial) {
    return [];
  }

  const progressiveAchievementsTriggered: string[] = [];

  // Check each progressive achievement
  for (const achievement of allAchievements) {
    if (
      achievement.isProgressive &&
      achievement.check(gameReport, gameStatus)
    ) {
      // Increment the counter for this progressive achievement
      const newCount = await unifiedDataStore.incrementProgressiveAchievement(
        achievement.id,
      );
      progressiveAchievementsTriggered.push(achievement.id);
      console.log(
        `Progressive achievement ${achievement.id} incremented to ${newCount}x`,
      );
    }
  }

  return progressiveAchievementsTriggered;
};
